import type { AxiosRequestConfig, AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import type { RequestOptions, Result, UploadFileParams } from './type'
import type { CreateAxiosOptions } from './axiosTransform'
import axios from 'axios'
import qs from 'qs'
import { AxiosCanceler } from './axiosCancel'
import { isFunction } from '../../is'
import deepClone from '../../../functions/deepClone'
import { ContentTypeEnum, RequestEnum } from './httpEnum'

export * from './axiosTransform'

/**
 * @description:  axios module
 */
export class VAxios {
    private axiosInstance: AxiosInstance
    private readonly options: CreateAxiosOptions

    constructor(options: CreateAxiosOptions) {
        this.options = options
        this.axiosInstance = axios.create(options)
        this.setupInterceptors()
    }

    /**
     * @description:  Create axios instance
     */
    private createAxios(config: CreateAxiosOptions): void {
        this.axiosInstance = axios.create(config)
    }

    private getTransform() {
        const { transform } = this.options
        return transform
    }

    getAxios(): AxiosInstance {
        return this.axiosInstance
    }

    /**
     * @description: Reconfigure axios
     */
    configAxios(config: CreateAxiosOptions) {
        if (!this.axiosInstance) {
            return
        }
        this.createAxios(config)
    }

    /**
     * @description: Set general header
     */
    setHeader(headers: any): void {
        if (!this.axiosInstance) {
            return
        }
        Object.assign(this.axiosInstance.defaults.headers, headers)
    }

    /**
     * @description: Interceptor configuration 拦截器配置
     */
    private setupInterceptors() {
        const transform = this.getTransform()
        if (!transform) {
            return
        }
        const { requestInterceptors, requestInterceptorsCatch, responseInterceptors, responseInterceptorsCatch }
            = transform

        const axiosCanceler = new AxiosCanceler()

        // Request interceptor configuration processing
        axios.interceptors.request.use((config: AxiosRequestConfig) => {
            if (requestInterceptors && isFunction(requestInterceptors)) {
                config = requestInterceptors(config, this.options)
            }
            return config
        }, undefined)

        this.axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
            if (requestInterceptors && isFunction(requestInterceptors)) {
                config = requestInterceptors(config, this.options)

            }
            return config
        }, undefined)

        // Request interceptor error capture
        if (requestInterceptorsCatch && isFunction(requestInterceptorsCatch)) {
            axios.interceptors.request.use(undefined, requestInterceptorsCatch)
            this.axiosInstance.interceptors.request.use(undefined, requestInterceptorsCatch)
        }


        this.axiosInstance.interceptors.response.use((res: AxiosResponse<any>) => {
            res && axiosCanceler.removePending(res.config)
            if (responseInterceptors && isFunction(responseInterceptors)) {
                res = responseInterceptors(res)
            }
            return res
        }, (error) => {
            return responseInterceptorsCatch
                    && isFunction(responseInterceptorsCatch)
                    && responseInterceptorsCatch(error)
        })
    }

    /**
     * @description:  File Upload
     */
    uploadFile<T = any>(config: AxiosRequestConfig, params: UploadFileParams) {
        const formData = new window.FormData()
        const customFilename = params.name || 'file'

        if (params.filename) {
            formData.append(customFilename, params.file, params.filename)
        }
        else {
            formData.append(customFilename, params.file)
        }

        if (params.data) {
            Object.keys(params.data).forEach((key) => {
                const value = params.data![key]
                if (Array.isArray(value)) {
                    value.forEach((item) => {
                        formData.append(`${key}[]`, item)
                    })
                    return
                }

                formData.append(key, params.data![key])
            })
        }

        return this.axiosInstance.request<Result<T>>({
            ...config,
            method: 'POST',
            data: formData,
            headers: {
                'Content-type': ContentTypeEnum.FORM_DATA,
                // @ts-ignore
                ignoreCancelToken: true
            }
        })
    }

    // support form-data
    supportFormData(config: AxiosRequestConfig) {
        const headers = config.headers || this.options.headers
        const contentType = headers?.['Content-Type'] || headers?.['content-type']

        if (
            contentType !== ContentTypeEnum.FORM_URLENCODED
            || !Reflect.has(config, 'data')
            || config.method?.toUpperCase() === RequestEnum.GET
        ) {
            return config
        }

        return {
            ...config,
            data: qs.stringify(config.data, { arrayFormat: 'brackets' })
        }
    }

    get<RT = any>(url: string, data: any = {}, options?: RequestOptions): Promise<Result<RT>> {
        return this.request<Result<RT>>(
            {
                url,
                params: data,
                method: 'GET'
            },
            options
        )
    }

    post<RT = any>(url: string, data: any = {}, options?: RequestOptions): Promise<Result<RT>> {
        return this.request<Result<RT>>(
            {
                url,
                data,
                method: 'POST'
            },
            options
        )
    }

    postWithJson<RT = any>(url: string, data: any = {}, options?: RequestOptions): Promise<Result<RT>> {
        return this.post<RT>(url, data, options)
    }

    put<RT = any>(url: string, data: any = {}, options?: RequestOptions): Promise<Result<RT>> {
        return this.request<Result<RT>>(
            {
                url,
                data,
                method: 'PUT'
            },
            options
        )
    }

    delete<RT = any>(url: string, data: any = {}, options?: RequestOptions): Promise<Result<RT>> {
        return this.request<Result<RT>>(
            {
                url,
                params: data,
                method: 'DELETE'
            },
            options
        )
    }

    postForm<RT = any>(url: string, data: any = {}): Promise<Result<RT>> {
        return this.request<Result<RT>>({
            url,
            data,
            method: 'POST',
            headers: {
                'Content-Type': ContentTypeEnum.FORM_URLENCODED
            }
        })
    }

    request<T = any>(config: AxiosRequestConfig, options?: RequestOptions): Promise<T> {
        let conf: CreateAxiosOptions = deepClone(config)
        const transform = this.getTransform()

        const { requestOptions } = this.options

        const opt: RequestOptions = Object.assign({}, requestOptions, options)

        const { beforeRequestHook, requestCatchHook, transformResponseHook } = transform || {}
        if (beforeRequestHook && isFunction(beforeRequestHook)) {
            conf = beforeRequestHook(conf, opt)
        }
        conf.requestOptions = opt

        conf = this.supportFormData(conf)
        return new Promise((resolve, reject) => {
            this.axiosInstance
                .request<any, AxiosResponse<Result>>(conf)
                .then((res: AxiosResponse<Result>) => {
                    if (transformResponseHook && isFunction(transformResponseHook)) {
                        try {
                            const ret = transformResponseHook(res, opt)
                            resolve(ret)
                        }
                        catch (err) {
                            reject(err || new Error('request error!'))
                        }
                        return
                    }
                    resolve(res as unknown as Promise<T>)
                })
                .catch((e: Error | AxiosError) => {
                    if (requestCatchHook && isFunction(requestCatchHook)) {
                        reject(requestCatchHook(e, opt))
                        return
                    }
                    if (axios.isAxiosError(e)) {
                        // rewrite error message from axios in here
                    }
                    reject(e)
                })
        })
    }
}
